import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Button,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Snackbar, Alert, IconButton, InputAdornment,
    Select, MenuItem, FormControl, InputLabel, Grid, Box,
    Card, CardMedia, FormControlLabel, Checkbox, Pagination,
    CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    Eye as Visibility, EyeOff as VisibilityOff, Upload, Image,
    Film, Trash2, Edit
} from 'lucide-react';
import { Add as AddIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import api from '../utils/api';
import UploadProgressDialog from './UploadProgressDialog';
import { DEFAULT_PLACEHOLDER } from './LazyImage';

const Products = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [availableLocations, setAvailableLocations] = useState([]);
    const [openEdit, setOpenEdit] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [openFiles, setOpenFiles] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [editedProduct, setEditedProduct] = useState({});
    const [editedLocationStock, setEditedLocationStock] = useState([]);
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [isUpdatingStock, setIsUpdatingStock] = useState(false);

    // Pagination states
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');

    // Upload progress states
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [currentFile, setCurrentFile] = useState('');
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [totalFiles, setTotalFiles] = useState(0);

    const userRole = localStorage.getItem('userRole');
    const userLocation = localStorage.getItem('userLocation');
    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
        // fetchData is defined below and uses page, searchTerm - intentionally excluded
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchTerm]);

    useEffect(() => {
        fetchCategories();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [productsResponse, locationsResponse] = await Promise.all([
                api.get('/products/paginated', {
                    params: { page, limit: 24, search: searchTerm }
                }),
                api.get('/config/locations')
            ]);
            setProducts(productsResponse.data.products);
            setPagination(productsResponse.data.pagination);
            setAvailableLocations(locationsResponse.data.storageLocations);
        } catch (error) {
            console.error('Error fetching data:', error);
            setSnackbar({ open: true, message: 'Failed to fetch products', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await api.get('/categories');
            setCategories(response.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const handleSearchChange = (e) => {
        setSearchInput(e.target.value);
    };

    const handleSearchSubmit = () => {
        setSearchTerm(searchInput);
        setPage(1);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        window.scrollTo(0, 0);
    };

    const handleEditClick = (product) => {
      if (!product || !product.category) {
          console.error('Invalid product data:', product);
          setSnackbar({
              open: true,
              message: 'Unable to edit product: Invalid product data',
              severity: 'error'
          });
          return;
      }

      setSelectedProduct(product);
      setEditedProduct({
          productName: product.productName,
          description: product.description || '',
          category: product.category._id || product.category,
          rateIncludesGST: product.rateIncludesGST || false,
          preferredLocation: product.preferredLocation || ''
      });

      // Initialize location stock (filter for non-universal employees)
      const shouldFilterByLocation = userRole === 'employee' && userLocation && userLocation !== 'Universal';

      if (product.locationStock && product.locationStock.length > 0) {
          const stockToEdit = shouldFilterByLocation
              ? product.locationStock.filter(ls => ls.location === userLocation)
              : product.locationStock;
          setEditedLocationStock(stockToEdit.map(ls => ({ ...ls })));
      } else {
          setEditedLocationStock([{ location: userLocation || 'Mill', stock: product.netStock || 0 }]);
      }

      setIsUpdatingStock(false); // Reset stock update flag
      setPassword(''); // Clear password
      setOpenEdit(true);
  };

    const handleDeleteClick = (product) => {
        setSelectedProduct(product);
        setOpenDelete(true);
    };

    const handleFilesClick = (product) => {
        setSelectedProduct(product);
        setOpenFiles(true);
    };

    const handleEditChange = (event) => {
        setEditedProduct({ ...editedProduct, [event.target.name]: event.target.value });
    };

    const handleLocationStockChange = (index, field, value) => {
        const updated = [...editedLocationStock];
        updated[index][field] = field === 'stock' ? parseFloat(value) || 0 : value;
        setEditedLocationStock(updated);
    };

    const addLocationStock = () => {
        setEditedLocationStock([...editedLocationStock, { location: '', stock: 0 }]);
    };

    const removeLocationStock = (index) => {
        if (editedLocationStock.length > 1) {
            setEditedLocationStock(editedLocationStock.filter((_, i) => i !== index));
        }
    };

    const handleEditSubmit = async () => {
        try {
            const updateData = {
                ...editedProduct
            };

            // Only include stock and password if owner is updating stock
            if (userRole === 'owner' && isUpdatingStock) {
                const filteredLocationStock = editedLocationStock.filter(ls => ls.location.trim() !== '');

                // Check for duplicate locations
                const locations = filteredLocationStock.map(ls => ls.location.trim().toLowerCase());
                const uniqueLocations = new Set(locations);
                if (locations.length !== uniqueLocations.size) {
                    setSnackbar({ open: true, message: 'Duplicate locations are not allowed', severity: 'error' });
                    return;
                }

                updateData.locationStock = filteredLocationStock;
                updateData.password = password;
            }

            await api.put(`/products/${selectedProduct._id}`, updateData);
            setSnackbar({ open: true, message: 'Product updated successfully', severity: 'success' });
            fetchData();
            setOpenEdit(false);
            setPassword(''); // Clear password after successful update
            setIsUpdatingStock(false); // Reset stock update flag
        } catch (error) {
            console.error('Error updating product:', error);
            setSnackbar({
                open: true,
                message: error.response?.data?.message || 'Failed to update product',
                severity: 'error'
            });
        }
    };

    const handleDeleteSubmit = async () => {
        try {
            await api.delete(`/products/${selectedProduct._id}`, { data: { password } });
            setSnackbar({ open: true, message: 'Product deleted successfully', severity: 'success' });
            fetchData();
            setOpenDelete(false);
        } catch (error) {
            console.error('Error deleting product:', error);
            setSnackbar({ 
                open: true, 
                message: error.response?.data?.message || 'Failed to delete product', 
                severity: 'error' 
            });
        }
    };

    const handleFileSelect = (event) => {
        setSelectedFiles(Array.from(event.target.files));
    };

    const handleFileUpload = async () => {
        try {
            const formData = new FormData();
            selectedFiles.forEach(file => {
                formData.append('files', file);
            });

            setIsUploading(true);
            setTotalFiles(selectedFiles.length);
            setCurrentFileIndex(1);
            setCurrentFile(selectedFiles.length === 1 ? selectedFiles[0].name : `${selectedFiles.length} files`);
            setUploadProgress(0);

            await api.post(`/products/${selectedProduct._id}/upload`, formData, {
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                }
            });

            setIsUploading(false);
            setSnackbar({ open: true, message: 'Files uploaded successfully', severity: 'success' });
            fetchData();
            setSelectedFiles([]);
        } catch (error) {
            setIsUploading(false);
            console.error('Error uploading files:', error);
            setSnackbar({
                open: true,
                message: 'Failed to upload files',
                severity: 'error'
            });
        }
    };

    const handleFileDelete = async (filename) => {
        try {
            await api.delete(`/products/${selectedProduct._id}/files/${filename}`);
            setSnackbar({ open: true, message: 'File deleted successfully', severity: 'success' });
            fetchData();
        } catch (error) {
            console.error('Error deleting file:', error);
            setSnackbar({ 
                open: true, 
                message: 'Failed to delete file', 
                severity: 'error' 
            });
        }
    };

    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
            <Box>
              <Typography
                  variant="h4"
                  sx={{
                      fontWeight: 700,
                      color: 'primary.main',
                      mb: 0.5
                  }}
              >
                  Products
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your product inventory
              </Typography>
            </Box>
            {userRole === 'owner' && (
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/add-product')}
                    sx={{
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                      }
                    }}
                >
                    Add New Product
                </Button>
            )}
          </Box>

          {/* Search Box */}
          <Box sx={{ mb: 3 }}>
              <TextField
                  fullWidth
                  label="Search Products"
                  value={searchInput}
                  onChange={handleSearchChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                  sx={{ bgcolor: 'white', borderRadius: 2 }}
                  InputProps={{
                      endAdornment: (
                          <InputAdornment position="end">
                              <IconButton onClick={handleSearchSubmit} color="primary">
                                  <SearchIcon />
                              </IconButton>
                          </InputAdornment>
                      )
                  }}
              />
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                  {loading ? 'Loading...' : `${pagination.totalRecords || 0} products found`}
              </Typography>
          </Box>

          <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
              <Table>
                  <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.main' }}>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Product Name</Typography></TableCell>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Description</Typography></TableCell>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Category</Typography></TableCell>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Rate GST</Typography></TableCell>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Preferred Location</Typography></TableCell>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Current Stock</Typography></TableCell>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Reserved Stock</Typography></TableCell>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Available Stock</Typography></TableCell>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Files</Typography></TableCell>
                          <TableCell><Typography variant="subtitle2" fontWeight="bold" color="white">Actions</Typography></TableCell>
                      </TableRow>
                  </TableHead>
                  <TableBody>
                      {loading ? (
                          <TableRow>
                              <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                                  <CircularProgress />
                                  <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                                      Loading products...
                                  </Typography>
                              </TableCell>
                          </TableRow>
                      ) : products.length === 0 ? (
                          <TableRow>
                              <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                                  <Typography variant="body1" color="text.secondary">
                                      No products found
                                  </Typography>
                              </TableCell>
                          </TableRow>
                      ) : products.map((product) => {
                          // Filter location stock for non-universal employees
                          const shouldFilterByLocation = userRole === 'employee' && userLocation && userLocation !== 'Universal';

                          const filteredLocationStock = shouldFilterByLocation
                              ? product.locationStock?.filter(ls => ls.location === userLocation)
                              : product.locationStock;

                          const filteredReservedStock = shouldFilterByLocation
                              ? product.reservedStock?.filter(rs => rs.location === userLocation)
                              : product.reservedStock;

                          // Calculate available stock per location
                          const availableByLocation = filteredLocationStock?.map(ls => {
                              const reserved = filteredReservedStock?.find(rs => rs.location === ls.location);
                              return {
                                  location: ls.location,
                                  current: ls.stock,
                                  reserved: reserved?.reserved || 0,
                                  available: ls.stock - (reserved?.reserved || 0)
                              };
                          }) || [];

                          return (
                          <TableRow
                            key={product._id}
                            hover
                            sx={{
                              '&:hover': { bgcolor: 'action.hover' },
                              transition: 'background-color 0.2s'
                            }}
                          >
                              <TableCell><Typography variant="body2" fontWeight={500}>{product.productName}</Typography></TableCell>
                              <TableCell>{product.description}</TableCell>
                              <TableCell>{product.category?.name}</TableCell>
                              <TableCell>
                                  <Typography variant="body2" color={product.rateIncludesGST ? 'success.main' : 'text.secondary'}>
                                      {product.rateIncludesGST ? 'Incl. GST' : 'Excl. GST'}
                                  </Typography>
                              </TableCell>

                              {/* Preferred Location */}
                              <TableCell>
                                  <Typography variant="body2" color={product.preferredLocation ? 'primary.main' : 'text.secondary'}>
                                      {product.preferredLocation || '-'}
                                  </Typography>
                              </TableCell>

                              {/* Current Stock */}
                              <TableCell>
                                  {filteredLocationStock && filteredLocationStock.length > 0 ? (
                                      <Box>
                                          {filteredLocationStock.map((ls, idx) => (
                                              <Typography key={idx} variant="body2">
                                                  {ls.location}: {ls.stock}
                                              </Typography>
                                          ))}
                                          {!shouldFilterByLocation && (
                                              <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                                  Total: {product.netStock || 0}
                                              </Typography>
                                          )}
                                      </Box>
                                  ) : (
                                      shouldFilterByLocation ? 0 : (product.netStock || 0)
                                  )}
                              </TableCell>

                              {/* Reserved Stock */}
                              <TableCell>
                                  {filteredReservedStock && filteredReservedStock.length > 0 ? (
                                      <Box>
                                          {filteredReservedStock.map((rs, idx) => (
                                              <Typography key={idx} variant="body2" color="warning.main">
                                                  {rs.location}: {rs.reserved}
                                              </Typography>
                                          ))}
                                          {!shouldFilterByLocation && (
                                              <Typography variant="caption" fontWeight="bold" color="warning.main">
                                                  Total: {product.netReservedStock || 0}
                                              </Typography>
                                          )}
                                      </Box>
                                  ) : (
                                      <Typography variant="body2" color="text.secondary">0</Typography>
                                  )}
                              </TableCell>

                              {/* Available Stock (Current - Reserved) */}
                              <TableCell>
                                  {availableByLocation.length > 0 ? (
                                      <Box>
                                          {availableByLocation.map((item, idx) => (
                                              <Typography
                                                  key={idx}
                                                  variant="body2"
                                                  color={item.available > 0 ? 'success.main' : 'error.main'}
                                              >
                                                  {item.location}: {item.available}
                                              </Typography>
                                          ))}
                                          {!shouldFilterByLocation && (
                                              <Typography variant="caption" fontWeight="bold" color="success.main">
                                                  Total: {(product.netStock || 0) - (product.netReservedStock || 0)}
                                              </Typography>
                                          )}
                                      </Box>
                                  ) : (
                                      <Typography variant="body2" color="success.main">
                                          {shouldFilterByLocation ? 0 : (product.netStock || 0)}
                                      </Typography>
                                  )}
                              </TableCell>
                              <TableCell>
                                  <Box className="flex items-center space-x-2">
                                      <Typography color="text.secondary">
                                          {product.images?.length || 0} <Image size={24} className="inline" />
                                      </Typography>
                                      <Typography color="text.secondary">
                                          {product.video ? 1 : 0} <Film size={24} className="inline" />
                                      </Typography>
                                      {userRole === 'owner' && (
                                          <IconButton
                                              onClick={() => handleFilesClick(product)}
                                              size="small"
                                              color="primary"
                                          >
                                              <Visibility size={24} />
                                          </IconButton>
                                      )}
                                  </Box>
                              </TableCell>
                              <TableCell>
                                  <Button 
                                      onClick={() => handleEditClick(product)}
                                      variant="contained"
                                      size="small"
                                      startIcon={<Edit />}
                                      sx={{ mr: 1, bgcolor: 'primary.main', color: '#fff' }}
                                  >
                                  </Button>
                                  {userRole === 'owner' && (
                                      <Button
                                          onClick={() => handleDeleteClick(product)}
                                          variant="contained"
                                          color="error"
                                          size="small"
                                          startIcon={<Trash2 />}
                                      >
                                      </Button>
                                  )}
                              </TableCell>
                          </TableRow>
                          );
                      })}
                  </TableBody>
              </Table>
          </TableContainer>

          {/* Pagination Controls */}
          {!loading && pagination.total > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <Pagination
                      count={pagination.total}
                      page={page}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                  />
              </Box>
          )}
      
          {/* Edit Dialog */}
          <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="md" fullWidth>
              <DialogTitle sx={{ fontWeight: 'bold' }}>Edit Product</DialogTitle>
              <DialogContent>
                  <TextField
                      fullWidth
                      label="Product Name"
                      name="productName"
                      value={editedProduct.productName || ''}
                      margin="normal"
                      required
                      disabled
                      helperText="Product name cannot be changed to maintain data integrity with orders"
                  />
                  <TextField
                      fullWidth
                      label="Description"
                      name="description"
                      value={editedProduct.description || ''}
                      onChange={handleEditChange}
                      margin="normal"
                      multiline
                      rows={3}
                  />
                  <FormControl fullWidth margin="normal">
                      <InputLabel>Category</InputLabel>
                      <Select
                          name="category"
                          value={editedProduct.category || ''}
                          onChange={handleEditChange}
                          label="Category"
                          required
                      >
                          {categories.map((category) => (
                              <MenuItem key={category._id} value={category._id}>
                                  {category.name}
                              </MenuItem>
                          ))}
                      </Select>
                  </FormControl>

                  <FormControlLabel
                      control={
                          <Checkbox
                              checked={editedProduct.rateIncludesGST || false}
                              onChange={(e) => setEditedProduct({ ...editedProduct, rateIncludesGST: e.target.checked })}
                              name="rateIncludesGST"
                          />
                      }
                      label="Product rates include GST"
                      sx={{ mt: 2 }}
                  />

                  <FormControl fullWidth margin="normal">
                      <InputLabel>Preferred Location</InputLabel>
                      <Select
                          name="preferredLocation"
                          value={editedProduct.preferredLocation || ''}
                          onChange={handleEditChange}
                          label="Preferred Location"
                      >
                          <MenuItem value="">
                              <em>No Preference</em>
                          </MenuItem>
                          {availableLocations.map((loc) => (
                              <MenuItem key={loc} value={loc}>
                                  {loc}
                              </MenuItem>
                          ))}
                      </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                      Orders will preferably use this location if sufficient stock is available
                  </Typography>

                  {userRole === 'owner' && (
                      <Box sx={{ mt: 3, mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Typography variant="h6">Stock Management</Typography>
                              <Button
                                  variant={isUpdatingStock ? "contained" : "outlined"}
                                  color={isUpdatingStock ? "primary" : "inherit"}
                                  onClick={() => setIsUpdatingStock(!isUpdatingStock)}
                                  size="small"
                              >
                                  {isUpdatingStock ? "Cancel Stock Update" : "Update Stock"}
                              </Button>
                          </Box>

                          {isUpdatingStock && (
                              <>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                      <Typography variant="subtitle2" color="text.secondary">
                                          Location Stock
                                      </Typography>
                                      <Button
                                          variant="outlined"
                                          startIcon={<AddIcon />}
                                          onClick={addLocationStock}
                                          size="small"
                                      >
                                          Add Location
                                      </Button>
                                  </Box>
                                  <Grid container spacing={2}>
                                      {editedLocationStock.map((ls, index) => (
                                          <React.Fragment key={index}>
                                              <Grid item xs={5}>
                                                  <FormControl fullWidth required>
                                                      <InputLabel>{`Location ${index + 1}`}</InputLabel>
                                                      <Select
                                                          value={ls.location}
                                                          onChange={(e) => handleLocationStockChange(index, 'location', e.target.value)}
                                                          label={`Location ${index + 1}`}
                                                          size="small"
                                                      >
                                                          {availableLocations.map((loc) => (
                                                              <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                                                          ))}
                                                      </Select>
                                                  </FormControl>
                                              </Grid>
                                              <Grid item xs={5}>
                                                  <TextField
                                                      fullWidth
                                                      label="Stock"
                                                      type="number"
                                                      value={ls.stock}
                                                      onChange={(e) => handleLocationStockChange(index, 'stock', e.target.value)}
                                                      onFocus={(e) => e.target.select()}
                                                      variant="outlined"
                                                      required
                                                      size="small"
                                                      inputProps={{ min: 0, step: 0.01 }}
                                                  />
                                              </Grid>
                                              <Grid item xs={2} sx={{ display: 'flex', alignItems: 'center' }}>
                                                  <IconButton
                                                      onClick={() => removeLocationStock(index)}
                                                      disabled={editedLocationStock.length === 1}
                                                      color="error"
                                                      size="small"
                                                  >
                                                      <DeleteIcon />
                                                  </IconButton>
                                              </Grid>
                                          </React.Fragment>
                                      ))}
                                  </Grid>
                              </>
                          )}
                      </Box>
                  )}

                  {userRole === 'owner' && isUpdatingStock && (
                      <TextField
                          fullWidth
                          type={showPassword ? 'text' : 'password'}
                          label="Enter your password to confirm changes"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          margin="normal"
                          required
                          InputProps={{
                              endAdornment: (
                                  <InputAdornment position="end">
                                      <IconButton
                                          onClick={() => setShowPassword(!showPassword)}
                                          edge="end"
                                      >
                                          {showPassword ? <VisibilityOff /> : <Visibility />}
                                      </IconButton>
                                  </InputAdornment>
                              ),
                          }}
                      />
                  )}
              </DialogContent>
              <DialogActions>
                  <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
                  <Button onClick={handleEditSubmit} variant="contained" color="primary">
                      Save Changes
                  </Button>
              </DialogActions>
          </Dialog>
      
          {/* Delete Dialog */}
          <Dialog open={openDelete} onClose={() => setOpenDelete(false)} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ fontWeight: 'bold' }}>Delete Product</DialogTitle>
              <DialogContent>
                  <Typography gutterBottom>
                      Are you sure you want to delete <strong>{selectedProduct?.productName}</strong>? 
                      This action cannot be undone.
                  </Typography>
                  <TextField
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      label="Enter your password to confirm"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      margin="normal"
                      required
                      InputProps={{
                          endAdornment: (
                              <InputAdornment position="end">
                                  <IconButton
                                      onClick={() => setShowPassword(!showPassword)}
                                      edge="end"
                                  >
                                      {showPassword ? <VisibilityOff /> : <Visibility />}
                                  </IconButton>
                              </InputAdornment>
                          ),
                      }}
                  />
              </DialogContent>
              <DialogActions>
                  <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
                  <Button onClick={handleDeleteSubmit} color="error" variant="contained">
                      Delete
                  </Button>
              </DialogActions>
          </Dialog>
      
          {/* Files Dialog */}
          <Dialog
              open={openFiles}
              onClose={() => setOpenFiles(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{
                  sx: {
                      borderRadius: 3,
                      boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                  }
              }}
          >
              <DialogTitle
                  sx={{
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: '#fff',
                      py: 2.5,
                      fontSize: '1.5rem'
                  }}
              >
                  Manage Files - {selectedProduct?.productName}
              </DialogTitle>
              <DialogContent dividers sx={{ bgcolor: '#f5f7fa', p: 3 }}>

                  {/* Images Section */}
                  <Box sx={{ mb: 4 }}>
                      <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                              fontWeight: 600,
                              color: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 2
                          }}
                      >
                          <Image size={24} />
                          Images Gallery
                      </Typography>
                      {selectedProduct?.images?.length > 0 ? (
                          <Grid container spacing={2}>
                              {selectedProduct.images.map((image, index) => (
                                  <Grid item xs={12} sm={6} md={4} key={index}>
                                      <Card
                                          sx={{
                                              position: 'relative',
                                              borderRadius: 3,
                                              overflow: 'hidden',
                                              transition: 'all 0.3s ease',
                                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                              '&:hover': {
                                                  transform: 'translateY(-4px)',
                                                  boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                                              }
                                          }}
                                      >
                                          <CardMedia
                                              component="img"
                                              height="180"
                                              image={image.url}
                                              alt={`Product image ${index + 1}`}
                                              sx={{
                                                  objectFit: 'cover',
                                                  cursor: 'pointer'
                                              }}
                                              onError={(e) => {
                                                  e.target.onerror = null;
                                                  e.target.src = DEFAULT_PLACEHOLDER;
                                              }}
                                          />
                                          <IconButton
                                              color="error"
                                              onClick={() => handleFileDelete(image.filename)}
                                              sx={{
                                                  position: 'absolute',
                                                  top: 8,
                                                  right: 8,
                                                  bgcolor: 'rgba(255, 255, 255, 0.95)',
                                                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                  '&:hover': {
                                                      bgcolor: 'rgba(255, 255, 255, 1)',
                                                      transform: 'scale(1.1)'
                                                  },
                                                  transition: 'all 0.2s'
                                              }}
                                          >
                                              <Trash2 size={18} />
                                          </IconButton>
                                      </Card>
                                  </Grid>
                              ))}
                          </Grid>
                      ) : (
                          <Box
                              sx={{
                                  p: 4,
                                  textAlign: 'center',
                                  bgcolor: 'white',
                                  borderRadius: 2,
                                  border: '2px dashed',
                                  borderColor: 'divider'
                              }}
                          >
                              <Image size={48} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
                              <Typography color="text.secondary">No images uploaded yet</Typography>
                          </Box>
                      )}
                  </Box>

                  {/* Video Section */}
                  <Box sx={{ mb: 4 }}>
                      <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                              fontWeight: 600,
                              color: 'primary.main',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 2
                          }}
                      >
                          <Film size={24} />
                          Product Video
                      </Typography>
                      {selectedProduct?.video ? (
                          <Box
                              sx={{
                                  position: 'relative',
                                  borderRadius: 3,
                                  overflow: 'hidden',
                                  boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                                  bgcolor: 'black'
                              }}
                          >
                              <video
                                  src={selectedProduct.video.url}
                                  controls
                                  style={{
                                      width: '100%',
                                      maxHeight: '400px',
                                      display: 'block'
                                  }}
                              />
                              <IconButton
                                  color="error"
                                  onClick={() => handleFileDelete(selectedProduct.video.filename)}
                                  sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      bgcolor: 'rgba(255, 255, 255, 0.95)',
                                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                      '&:hover': {
                                          bgcolor: 'rgba(255, 255, 255, 1)',
                                          transform: 'scale(1.1)'
                                      },
                                      transition: 'all 0.2s'
                                  }}
                              >
                                  <Trash2 size={18} />
                              </IconButton>
                          </Box>
                      ) : (
                          <Box
                              sx={{
                                  p: 4,
                                  textAlign: 'center',
                                  bgcolor: 'white',
                                  borderRadius: 2,
                                  border: '2px dashed',
                                  borderColor: 'divider'
                              }}
                          >
                              <Film size={48} style={{ opacity: 0.3, margin: '0 auto 8px' }} />
                              <Typography color="text.secondary">No video uploaded yet</Typography>
                          </Box>
                      )}
                  </Box>

                  {/* Upload Section */}
                  <Box
                      sx={{
                          p: 3,
                          bgcolor: 'white',
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'divider'
                      }}
                  >
                      <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 2
                          }}
                      >
                          <Upload size={24} />
                          Upload New Files
                      </Typography>
                      <input
                          type="file"
                          multiple
                          accept="image/*,video/*"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                          id="file-input"
                      />
                      <label htmlFor="file-input">
                          <Button
                              variant="outlined"
                              component="span"
                              startIcon={<Upload />}
                              sx={{
                                  borderRadius: 2,
                                  px: 3,
                                  py: 1.5,
                                  borderWidth: 2,
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                  '&:hover': {
                                      borderWidth: 2,
                                      bgcolor: 'primary.main',
                                      color: '#fff',
                                  },
                              }}
                          >
                              Select Files
                          </Button>
                      </label>

                      {selectedFiles.length > 0 && (
                          <Box
                              sx={{
                                  mt: 2,
                                  p: 2,
                                  bgcolor: 'primary.lighter',
                                  borderRadius: 2,
                                  border: '1px solid',
                                  borderColor: 'primary.main'
                              }}
                          >
                              <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                                  Selected files: {selectedFiles.map(f => f.name).join(', ')}
                              </Typography>
                              <Button
                                  variant="contained"
                                  onClick={handleFileUpload}
                                  sx={{
                                      mt: 1,
                                      borderRadius: 2,
                                      px: 3,
                                      py: 1,
                                      textTransform: 'none',
                                      fontWeight: 600,
                                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                                      '&:hover': {
                                          boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                                      }
                                  }}
                              >
                                  Upload Files
                              </Button>
                          </Box>
                      )}
                  </Box>
              </DialogContent>
              <DialogActions sx={{ p: 2.5, bgcolor: 'white' }}>
                  <Button
                      onClick={() => setOpenFiles(false)}
                      sx={{
                          px: 3,
                          py: 1,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600
                      }}
                  >
                      Close
                  </Button>
              </DialogActions>
          </Dialog>

          {/* Upload Progress Dialog */}
          <UploadProgressDialog
              open={isUploading}
              progress={uploadProgress}
              fileName={currentFile}
              totalFiles={totalFiles}
              currentFile={currentFileIndex}
          />

          <Snackbar
              open={snackbar.open}
              autoHideDuration={6000}
              onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
              <Alert
                  onClose={() => setSnackbar({ ...snackbar, open: false })}
                  severity={snackbar.severity}
              >
                  {snackbar.message}
              </Alert>
          </Snackbar>
      </Container>
    );
};

export default Products;